/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {RotateArrowIcon} from '@ion/dls/src/icons/RotateArrowIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';

export interface TiltSensorStatusProps {
  isOverThreshold?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    tiltBox : {
      height          : 95,
      width           : 55,
      border          : `2.5px solid ${dlsColor('caGreen')}`,
      backgroundColor : dlsColor('paleBlue')
    },
    tiltBoxTilted : {
      height          : 95,
      width           : 55,
      border          : `2.5px solid ${dlsColor('caRed')}`,
      backgroundColor : dlsColor('paleBlue'),
      transform       : 'rotate(30deg)',
      marginBottom    : '8px'
    },
    tiltedArrow : {
      position                       : 'absolute',
      bottom                         : 32,
      left                           : 25,
      [theme.breakpoints.down('sm')] : {
        left : 30
      }
    },
    foundationBase : {
      height          : 20,
      width           : '100%',
      borderTop       : `2.5px solid ${dlsColor('caGreen')}`,
      backgroundColor : dlsColor('paleBlue')
    }
  };
});

/**
 * TiltSensorGraphic is a component used for displaying SC Device Tilt sensor data
 * via a box and foundation graphic to represent the if the device has been tilted.
 *
 * ```typescript
 *
 * import {TiltSensorGraphic} from '@/ion-suite/src/routes/tracking/scDetails/reports';
 *
 * <TiltSensorGraphic isOverThreshold={isOverThreshold} />
 *
 * ```
 *
 * @returns {JSX.Element}
 */
const TiltSensorGraphic = ({isOverThreshold}: TiltSensorStatusProps) => {
  const classes = useStyles();

  return (
    <>
      {isOverThreshold ? (
        <>
          <div className={classes.tiltBoxTilted} />
          <span className={classes.tiltedArrow}>
            <RotateArrowIcon color={dlsColor('caRed')} />
          </span>
        </>
      ) : (
        <div className={classes.tiltBox} />
      )}
      <div className={classes.foundationBase} style={{
        borderTopColor : isOverThreshold && `${dlsColor('caRed')}`
      }}/>
    </>
  );
};

export default TiltSensorGraphic;
